<template>
  <div class="bind-phone-container" :class="{ 'check-login-mode': fromPage === 'checkout' }">
    <div class="bind-phone-header">
      <h2>Bind Phone Number</h2>
      <p>Please verify your phone number to complete your account setup</p>
    </div>

    <div class="phone-verification-form">
      <div class="form-section">
        <h3>Phone Verification</h3>
        
        <!-- 手机号输入 -->
        <div class="input-group">
          <div class="phone-input-container">
            <div class="area-box">
              <PhoneBtn v-model="form.code"></PhoneBtn>
            </div>
            <div class="phone-input">
              <input
                v-model="form.phone"
                @keyup="form.phone = form.phone.replace(/\D/g, '')"
                placeholder="Phone number"
                type="tel"
                class="phone-field"
              />
            </div>
          </div>
        </div>

        <!-- 验证码输入 -->
        <div class="input-group">
          <div class="sms-input-container">
            <input
              v-model="form.verCode"
              @keyup="form.verCode = form.verCode.substring(0, 4)"
              placeholder="Enter verification code"
              type="text"
              class="sms-field"
              style="min-width: 100px;"
            />
            <div class="sms-actions">
              <button
                :class="['send-code-btn', { 'disabled': smsCountdown > 0 || isSendingSms }]"
                @click="sendSmsCode"
                :disabled="smsCountdown > 0 || isSendingSms"
              >
                <i v-if="isSendingSms" class="el-icon-loading"></i>
                <span v-else>{{ smsCountdown > 0 ? `${smsCountdown}s` : 'Send Code' }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="sms-channel-row">
          <div class="sms-channel-select">
            <span class="sms-channel-label">Send code to:</span>
            <div class="sms-channel-options">
              <label
                class="sms-channel-option"
                :class="{ active: smsChannel === 'whatsapp' }"
              >
                <input
                  type="radio"
                  value="whatsapp"
                  v-model="smsChannel"
                />
                <span>WhatsApp</span>
              </label>
              <label
                class="sms-channel-option"
                :class="{ active: smsChannel === 'sms' }"
              >
                <input
                  type="radio"
                  value="sms"
                  v-model="smsChannel"
                />
                <span>Phone</span>
              </label>
            </div>
          </div>
        </div>

        <div class="divider"></div>
        <h3>Personal Information</h3>
        <!-- First Name 输入 -->
        <div class="input-group">
          <div class="firstname-input-container">
            <input
              v-model="form.firstName"
              placeholder="Your Name (Optional)"
              type="text"
              class="firstname-field"
            />
          </div>
        </div>

        <!-- Email 输入 -->
        <div class="input-group" >
          <div class="email-input-container">
            <input
              v-model="form.email"
              placeholder="Email (Optional)"
              type="email"
              class="email-field"
            />
          </div>
          <div v-if="isApplePrivateRelayEmail" class="apple-email-tip">
            This is an Apple Private Relay email. Please check if you want to update it to your personal email address.
          </div>
        </div>

      </div>

      <!-- 提交按钮 -->
      <div class="submit-section">
        <button
          :class="['submit-btn', { 'loading': isSaving }]"
          @click="bindPhone"
          :disabled="isSaving || !canSubmit"
        >
          <i v-if="isSaving" class="el-icon-loading"></i>
          <span v-else>Submit</span>
        </button>
      </div>
    </div>

    <!-- Cloudflare验证弹层 -->
    <div v-if="showCloudflare && $cloudflareVerify" class="cloudflare-modal">
      <div class="cloudflare-overlay" @click="closeCloudflare"></div>
      <div class="cloudflare-content">
        <div class="cloudflare-header">
          <h3>Security Verification</h3>
          <button class="close-btn" @click="closeCloudflare">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <CloudflareVerify 
          style="padding: 20px;" 
          ref="cloudflare"
          @token-received="onTokenReceived"
        ></CloudflareVerify>
      </div>
    </div>

    <!-- 验证码滑块模块 -->
    <Verify 
      v-if="!$cloudflareVerify"
      @success="sendcodeMsg"
      :mode="'pop'"
      :captchaType="'blockPuzzle'"
      :imgSize="{ width: '330px', height: '155px' }"
      ref="verify"
    ></Verify>
  </div>
</template>

<script>
import { postGateway } from '@/request';
import { getSmsSendUrl } from '@/config/smsApiConfig';
import PhoneBtn from '@/components/PhoneBtn.vue';
import Verify from '@/components/Verifition/Verify';
import CloudflareVerify from '@/components/CloudFlareVerify.vue';
import bus from '@/utils/bus.js';

export default {
  name: 'BindPhone',
  components: {
    PhoneBtn,
    Verify,
    CloudflareVerify
  },
  props: {
    fromPage: {
      type: String,
      default: 'login'
    }
  },
  data() {
    return {
      form: {
        phone: '',
        code: (this.$isUk ? '+44' : '+353'),
        verCode: '',
        firstName: '',
        email: ''
      },
      isSaving: false,
      isSendingSms: false,
      smsChannel: 'whatsapp',
      smsCountdown: 0,
      sendMsgTimes: 0,
      showCloudflare: false,
      cloudflareToken: null,
      timer: null
    }
  },
  computed: {
    canSubmit() {
      return this.form.phone.trim() !== '' && 
             this.form.verCode.trim() !== '' && 
             this.form.verCode.length === 4;
    },
    isApplePrivateRelayEmail() {
      if (!this.form.email || !this.form.email.trim()) {
        return false;
      }
      return this.form.email.toLowerCase().includes('@privaterelay.appleid.com');
    }
  },
  mounted() {
    this.getUserInfo();
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    // 获取用户信息
    async getUserInfo() {
      try {
        const response = await postGateway({
          url: '/customerApp/center/getDetail',
          method: 'GET',
          data: {
            clientId: localStorage.getItem('clientId')
          }
        });

        if (response.code === 200 && response.data) {
          // 判断是否是 Apple User
          // Apple User 的判断条件：
          // 1. firstName 等于 'Apple User'
          // 2. 或者邮箱包含 '@apple.privaterelay.appleid.com'
          const isAppleUser = response.data.firstName === 'Apple User';
          
          // 如果是 Apple User，不填充 firstName（认为是空的）
          // 如果不是 Apple User，且有 firstName，就自动填充
          if (!isAppleUser && response.data.firstName) {
            this.form.firstName = response.data.firstName;
          }
          
          // 如果有 email 就自动填充
          if (response.data.emailAddress) {
            this.form.email = response.data.emailAddress;
          }
        }
      } catch (error) {
        // 静默处理错误，不影响主要功能
        console.log('获取用户信息失败:', error);
      }
    },

    // 校验手机号是否重复国家代码（仅针对 +44 和 +353）
    validatePhoneNumberFormat() {
      const phoneNumber = this.form.phone.trim();
      
      // 只处理 +44 和 +353 的情况
      if (this.form.code === '+44' && phoneNumber.startsWith('44')) {
        this.$message({
          message: "Please remove the country code '44' from the phone number",
          type: 'warning',
          offset: 50
        });
        return false;
      }
      
      if (this.form.code === '+353' && phoneNumber.startsWith('353')) {
        this.$message({
          message: "Please remove the country code '353' from the phone number",
          type: 'warning',
          offset: 50
        });
        return false;
      }
      
      return true;
    },

    // 发送短信验证码
    async sendSmsCode() {
      if (!this.form.phone.trim()) {
        this.$message({
          message: 'Please enter phone number',
          type: 'warning',
          offset: 50
        });
        return;
      }

      // 校验手机号是否重复国家代码
      if (!this.validatePhoneNumberFormat()) {
        return;
      }

      if (!this.isValidPhoneNumber(this.form.phone)) {
        this.$message({
          message: 'Please enter a valid phone number',
          type: 'warning',
          offset: 50
        });
        return;
      }

      // 根据配置选择验证方式
      if (this.$cloudflareVerify) {
        // 使用 Cloudflare 验证
        this.showCloudflare = true;
      } else {
        // 使用滑块验证
        this.$refs.verify.show();
      }
    },

    // 发送短信验证码（滑块验证回调或 Cloudflare 回调）
    async sendcodeMsg({ captchaVerification }) {
      // 如果是 Cloudflare 验证，captchaVerification 可能是字符串 token
      const token = captchaVerification || this.cloudflareToken;
      if (!token) {
        return;
      }

      try {
        this.isSendingSms = true;

        const url = getSmsSendUrl({
          cloudflare: !!this.$cloudflareVerify,
          isResend: this.sendMsgTimes > 0,
          channel: this.smsChannel || 'whatsapp',
        });

        const response = await postGateway({
          url: url,
          method: 'POST',
          data: {
            phone: this.form.code + this.form.phone.trim(),
            captchaType: 'register',
            captchaVerification: token,
            channel: this.smsChannel || 'whatsapp',
          }
        });

        if (response.code === 200) {
          // 发送成功，重置 Cloudflare 验证状态
          if (this.$cloudflareVerify) {
            this.cloudflareToken = null;
            this.showCloudflare = false;
          }

          this.$message({
            message: 'Verification code sent successfully',
            type: 'success',
            offset: 50
          });
          this.startSmsCountdown();
          this.sendMsgTimes++;
        } else {
          // 发送失败，清除 Cloudflare token，下次需要重新验证
          if (this.$cloudflareVerify) {
            this.cloudflareToken = null;
            this.showCloudflare = false;
          }

          this.$message.error(response.msg || 'Failed to send verification code');
        }
      } catch (error) {
        // 网络错误，清除 Cloudflare token，下次需要重新验证
        if (this.$cloudflareVerify) {
          this.cloudflareToken = null;
          this.showCloudflare = false;
        }

        this.$message.error(error.msg || 'Network error');
      } finally {
        this.isSendingSms = false;
      }
    },

    // 绑定手机号
    async bindPhone() {
      if (!this.form.phone.trim()) {
        this.$message({
          message: 'Please enter phone number',
          type: 'warning',
          offset: 50
        });
        return;
      }

      if (!this.form.verCode.trim()) {
        this.$message({
          message: 'Please enter verification code',
          type: 'warning',
          offset: 50
        });
        return;
      }

      try {
        this.isSaving = true;

        const response = await postGateway({
          url: '/customer/atClient/editPhone',
          method: 'POST',
          data: {
            captcha: this.form.verCode.trim(),
            clientId: localStorage.getItem('clientId'),
            contactPhone: this.form.code + this.form.phone.trim()
          }
        });

        if (response.code === 200) {
          this.$message({
            message: 'Phone number bound successfully',
            type: 'success',
            offset: 50
          });

          // 异步保存用户信息（如果有填写）
          if (this.form.firstName.trim() || this.form.email.trim()) {
            await this.saveUserInfo();
          }

          // 通知用户信息已更新，让上层组件刷新状态
          bus.$emit('userInfoUpdated');

          // 延迟跳转，确保消息显示
          setTimeout(() => {
            this.handleBindSuccess();
          }, 1000);
        } else {
          let errorMessage = 'Failed to bind phone number';
          if (response.code === 50020) {
            errorMessage = 'The phone number has been registered';
          } else if (response.code === 50002) {
            errorMessage = 'Incorrect verification code';
          } else if (response.code === 50003) {
            errorMessage = 'The email has been registered';
          } else if (response.msg) {
            errorMessage = response.msg;
          }
          this.$message.error(errorMessage);
        }
      } catch (error) {
        this.$message.error(error.msg || 'Network error');
      } finally {
        this.isSaving = false;
      }
    },

    // 处理绑定成功
    handleBindSuccess() {
      this.$emit('bindDone', true);
    },

    // 开始短信倒计时
    startSmsCountdown() {
      this.smsCountdown = 60;
      this.timer = setInterval(() => {
        this.smsCountdown--;
        if (this.smsCountdown <= 0) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }, 1000);
    },

    // 验证手机号格式
    isValidPhoneNumber(phone) {
      return phone.length >= 6;
    },

    // 关闭Cloudflare弹层
    closeCloudflare() {
      this.showCloudflare = false;
      this.cloudflareToken = null;
      this.isSendingSms = false; // 取消loading状态
    },

    // 接收Cloudflare token
    onTokenReceived(token) {
      this.cloudflareToken = token;
      this.showCloudflare = false;
      this.sendcodeMsg({ captchaVerification: token });
    },


    // 异步保存用户信息
    async saveUserInfo() {
      try {
        const data = {
          clientId: localStorage.getItem('clientId')
        };
        
        // 只保存有值的字段
        if (this.form.firstName.trim()) {
          data.firstName = this.form.firstName.trim();
        }
        if (this.form.email.trim()) {
          data.emailAddress = this.form.email.trim();
        }
        
        await postGateway({
          url: '/customer/atClient/savePersonalInfo',
          method: 'POST',
          data: data
        });
        console.log('User info saved successfully');
      } catch (error) {
        // 静默处理错误，不影响主要功能
        console.log('保存用户信息失败:', error);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bind-phone-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
  
  &.check-login-mode {
    padding: 16px;
  }

  .bind-phone-header {
    text-align: center;
    margin-bottom: 30px;

    h2 {
      font-size: 24px;
      font-weight: 700;
      color: #2c3e50;
      margin: 0 0 8px 0;
    }

    p {
      font-size: 14px;
      color: #666;
      margin: 0;
      line-height: 1.5;
    }
  }

  .phone-verification-form {
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    overflow: hidden;

    .form-section {
      padding: 20px;

      h3 {
        font-size: 16px;
        font-weight: 600;
        color: #2c3e50;
        margin: 0 0 20px 0;
      }

      .input-group {
        margin-bottom: 20px;

        .phone-input-container {
          display: flex;
          align-items: center;
          gap: 10px;

          .area-box {
            height: 48px;
            width: 100px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            border-radius: 8px;
          }

          .phone-input {
            flex: 1;

            .phone-field {
              width: 100%;
              height: 48px;
              border: 1px solid #e0e0e0;
              border-radius: 8px;
              padding: 0 16px;
              font-size: 16px;
              outline: none;
              transition: border-color 0.3s ease;

              &:focus {
                border-color: #09ca6a;
              }

              &::placeholder {
                color: #999;
              }
            }
          }
        }

        .firstname-input-container {
          .firstname-field {
            width: 100%;
            height: 48px;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 0 16px;
            font-size: 16px;
            outline: none;
            transition: border-color 0.3s ease;

            &:focus {
              border-color: #09ca6a;
            }

            &::placeholder {
              color: #999;
            }
          }
        }

        .email-input-container {
          .email-field {
            width: 100%;
            height: 48px;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 0 16px;
            font-size: 16px;
            outline: none;
            transition: border-color 0.3s ease;

            &:focus {
              border-color: #09ca6a;
            }

            &::placeholder {
              color: #999;
            }
          }
        }

        .apple-email-tip {
          margin-top: 8px;
          padding: 12px 16px;
          background-color: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 8px;
          font-size: 13px;
          line-height: 1.5;
          color: #856404;
        }

        .sms-input-container {
          display: flex;
          align-items: center;
          gap: 12px;

          .sms-field {
            flex: 1;
            height: 48px;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 0 16px;
            font-size: 16px;
            outline: none;
            transition: border-color 0.3s ease;

            &:focus {
              border-color: #09ca6a;
            }

            &::placeholder {
              color: #999;
            }
          }

          .send-code-btn {
            height: 48px;
            padding: 0 20px;
            background: #09ca6a;
            color: white;
            border: none;
            border-radius: 24px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            white-space: nowrap;
            min-width: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;

            &:hover:not(.disabled) {
              background: #08b35e;
              transform: translateY(-1px);
            }

            &.disabled {
              background: #ccc;
              cursor: not-allowed;
              transform: none;
            }

            i {
              font-size: 14px;
            }
          }

          .sms-actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 4px;
          }

          .sms-channel-row {
            margin-top: 8px;
            display: flex;
            justify-content: flex-start;
            width: 100%;
          }

          .sms-channel-select {
            display: inline-flex;
            align-items: center;
            font-size: 12px;
            color: #666;
            gap: 8px;
            flex-wrap: wrap;

            .sms-channel-label {
              white-space: nowrap;
            }

            .sms-channel-pill {
              border-radius: 999px;
              border: 1px solid #e0e0e0;
              padding: 4px 10px;
              background: #fff;
              box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);

              select {
                border: none;
                background: transparent;
                outline: none;
                font-size: 12px;
                color: #333;
                cursor: pointer;
              }
            }
            
            .sms-channel-options {
              display: flex;
              gap: 6px;
              flex-wrap: wrap;
            }

            .sms-channel-option {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 4px 10px 4px 8px;
              border-radius: 999px;
              border: 1px solid #e0e0e0;
              background: #fff;
              cursor: pointer;
              font-size: 12px;
              color: #666;

              input {
                appearance: none;
                width: 14px;
                height: 14px;
                border-radius: 50%;
                border: 2px solid #ccc;
                margin: 0;
              }

              &.active {
                border-color: #09ca6a;
                background: rgba(9, 202, 106, 0.06);
                color: #09ca6a;

                input {
                  border-color: #09ca6a;
                  box-shadow: inset 0 0 0 3px #09ca6a;
                }
              }
            }
          }
        }
      }

      .divider {
        height: 1px;
        background: #e9ecef;
        margin: 20px 0;
      }
    }

    .submit-section {
      padding: 20px;
      border-top: 1px solid #e9ecef;
      background: #f8f9fa;

      .submit-btn {
        width: 100%;
        height: 48px;
        background: #09ca6a;
        color: white;
        border: none;
        border-radius: 24px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        &:hover:not(.loading) {
          background: #08b35e;
          transform: translateY(-1px);
        }

        &.loading {
          background: #ccc;
          cursor: not-allowed;
          transform: none;
        }

        i {
          font-size: 16px;
        }
      }
    }
  }
}

// Cloudflare 弹层样式
.cloudflare-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  
  .cloudflare-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
  }
  
  .cloudflare-content {
    position: relative;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    max-width: 520px;
    width: 100%;
    max-height: 90vh;
    overflow: hidden;
    animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform-origin: center;
    
    .cloudflare-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 24px 28px;
      border-bottom: 1px solid #e9ecef;
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      
      h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        color: #2c3e50;
        display: flex;
        align-items: center;
      }
      
      .close-btn {
        background: none;
        border: none;
        font-size: 22px;
        color: #6c757d;
        cursor: pointer;
        padding: 8px;
        border-radius: 8px;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        
        &:hover {
          background: #e9ecef;
          color: #495057;
          transform: scale(1.1);
        }
      }
    }
    
    // Cloudflare 组件容器
    :deep(.cloudflare-verify) {
      padding: 24px 28px 28px;
      text-align: center;
      
      .cloudflare-widget {
        margin: 0 auto;
        border-radius: 8px;
        overflow: hidden;
      }
    }
  }

  @media screen and (max-width: 768px) {
    .cloudflare-content {
      border-radius: 0;
      max-width: 100%;
      height: 100%;
      max-height: 100%;
    }
  }
}

@keyframes modalSlideIn {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-30px);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05) translateY(-5px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

// 移动端适配
@media (max-width: 480px) {
  .bind-phone-container {
    padding: 0px;
    
    &.check-login-mode {
      padding: 0px;
    }

    .bind-phone-header {
      h2 {
        font-size: 20px;
      }
      
      p {
        font-size: 13px;
      }
    }

    .phone-verification-form {
      .form-section {
        padding: 16px;

        .input-group {
          .phone-input-container {
            .area-box {
              width: 80px;
              height: 44px;
            }

            .phone-field {
              height: 44px;
              font-size: 14px;
            }
          }

          .firstname-input-container {
            .firstname-field {
              height: 44px;
              font-size: 14px;
            }
          }

          .email-input-container {
            .email-field {
              height: 44px;
              font-size: 14px;
            }
          }

          .apple-email-tip {
            font-size: 12px;
            padding: 10px 12px;
            margin-top: 6px;
          }

          .sms-input-container {
            .sms-field {
              height: 44px;
              font-size: 14px;
            }

            .send-code-btn {
              height: 44px;
              padding: 0 16px;
              font-size: 12px;
              min-width: 80px;
            }
          }
        }
      }

      .submit-section {
        padding: 16px;

        .submit-btn {
          height: 44px;
          font-size: 14px;
        }
      }
    }
  }
}
</style>
